import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { AuditorApprovalTable } from './AuditorApprovalTable';
import { getSOAList } from '../../api';
import { getPropertyList } from '../../../../Inventory/utils/api';
import { PropertySelector } from './PropertySelector';

const AuditorTableWrapper = () => {
    const dispatch = useDispatch();
    const [properties, setProperties] = useState([]);
    const [soas, setSOAs] = useState([]);
    const [user, setUser] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState({
        property_id: '',
        building_name: '',
        address: '',
        postal_code: '',
    });
    const [filters, setFilters] = useState({
        name: '',
        unitNo: '',
        dateRangeStart: null,
        dateRangeEnd: null,
        status: 'awaiting_auditor',
    });

    const getUserFromToken = useCallback(() => {
        try {
            const token = localStorage.getItem('accessToken');
            return token ? jwt_decode(token) : null;
        } catch {
            return null;
        }
    }, []);

    const fetchProperties = useCallback(async () => {
        const user = getUserFromToken();
        if (!user) return;

        setUser(user);

        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const result = await getPropertyList(user?.role || '', user?.id || '');
            setProperties(result?.propertyList || []);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                html: '<p class="text-lg">We couldn’t fetch the properties. Please try again later!</p>',
                confirmButtonColor: '#3085d6',
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [dispatch, getUserFromToken]);

    const fetchSOAs = useCallback(async (propertyId, buildingName, filters) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const { data } = await getSOAList({
                propertyId: propertyId || undefined, // Send undefined if no property is selected
                buildingName: buildingName || undefined,
                name: filters.name || undefined,
                unitNo: filters.unitNo || undefined,
                dateRangeStart: filters.dateRangeStart || undefined,
                dateRangeEnd: filters.dateRangeEnd || undefined,
                status: filters.status !== 'all' ? filters.status : undefined,
            });
            setSOAs(data || []);
        } catch (error) {
            console.error('[fetchSOAs] Error:', error);
            setSOAs([]);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [dispatch]);

    const handlePropertySelectionChange = useCallback((newValues) => {
        setSelectedProperty(newValues);
    }, []);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    const handleSuccess = useCallback(() => {
        fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, filters);
    }, [fetchSOAs, selectedProperty.property_id, selectedProperty.building_name, filters]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    useEffect(() => {
        fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, filters);
    }, [selectedProperty.property_id, selectedProperty.building_name, filters, fetchSOAs]);

    return (
        <div className="page-content">
            <MetaTags>
                <title>Rentdigicare | Auditor SOA Approval</title>
            </MetaTags>
            <div className="container-fluid">
                <Breadcrumbs
                    title="Property Details"
                    breadcrumbItem="Auditor SOA Approval"
                />
                <PropertySelector
                    propertyList={properties}
                    selectedProperty={selectedProperty}
                    onPropertySelectionChange={handlePropertySelectionChange}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    isLoading={false}
                    user={user}
                    hideAddButton={true}
                />
                <AuditorApprovalTable
                    items={soas}
                    onSuccess={handleSuccess}
                    fetchSOAs={fetchSOAs}
                />
            </div>
        </div>
    );
};

export { AuditorTableWrapper };